<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import FeatureGuide from '../pipelines/guide/FeatureGuide.vue';
import { buildApiBase } from '../pipelines/guide/buildApiBase';

const store = useStore();
const { t, tm, rt } = useI18n();

const reminders = useMapGetter('reminders/getReminders');
const uiFlags = useMapGetter('reminders/getUIFlags');
const contacts = useMapGetter('contacts/getContacts');
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');

// --- In-app guide ("How to use") -----------------------------------------
const isGuideOpen = ref(false);
const apiBase = computed(() =>
  buildApiBase(window.location.origin, accountId.value)
);
// Token da conta (para copiar/enviar requisições no playground do guia).
const apiToken = computed(() => currentUser.value?.access_token || '');

// Os corpos de request (JSON) ficam em JS, nunca no i18n (o vue-i18n trata `{...}`
// como placeholder e quebra a compilação). A ordem espelha REMINDERS.GUIDE.API.
const API_BODIES = [
  '',
  '{"reminder":{"title":"Call the customer","description":"Proposal sent","due_at":"2026-06-20T15:00:00Z","contact_id":42,"conversation_display_id":18}}',
  '{"reminder":{"status":"done"}}',
  '',
];

// Resolves the structured guide content from the i18n message tree. `tm`
// returns compiled message nodes, so `rt` is used to render leaf strings.
const guideContent = computed(() => ({
  overviewTitle: t('GUIDE.COMMON.OVERVIEW_TAB'),
  stepsTitle: t('GUIDE.COMMON.STEPS_TAB'),
  apiTitle: t('GUIDE.COMMON.API_TAB'),
  apiAuth: t('GUIDE.COMMON.API_AUTH'),
  apiTokenHint: t('GUIDE.COMMON.API_TOKEN_HINT'),
  overview: t('REMINDERS.GUIDE.OVERVIEW'),
  useCases: tm('REMINDERS.GUIDE.USE_CASES').map(useCase => ({
    icon: rt(useCase.ICON),
    title: rt(useCase.TITLE),
    description: rt(useCase.DESCRIPTION),
  })),
  steps: tm('REMINDERS.GUIDE.STEPS').map(step => rt(step)),
  api: tm('REMINDERS.GUIDE.API').map((endpoint, index) => ({
    title: rt(endpoint.TITLE),
    url: rt(endpoint.URL),
    body: API_BODIES[index] || '',
  })),
}));

const form = reactive({
  title: '',
  description: '',
  due_at: '',
  contact_id: null,
  conversation_display_id: '',
});

const hasReminders = computed(() => reminders.value.length > 0);

onMounted(() => {
  store.dispatch('reminders/get');
  store.dispatch('contacts/get');
});

const resetForm = () => {
  form.title = '';
  form.description = '';
  form.due_at = '';
  form.contact_id = null;
  form.conversation_display_id = '';
};

const createReminder = async () => {
  if (!form.title.trim()) return;
  await store.dispatch('reminders/create', {
    title: form.title.trim(),
    description: form.description,
    due_at: form.due_at || null,
    contact_id: form.contact_id,
    conversation_display_id: form.conversation_display_id || null,
  });
  resetForm();
};

const toggleStatus = reminder => {
  store.dispatch('reminders/update', {
    id: reminder.id,
    status: reminder.status === 'done' ? 'pending' : 'done',
  });
};

const deleteReminder = id => {
  store.dispatch('reminders/delete', id);
};

const formatDate = value => (value ? new Date(value).toLocaleString() : '');

const contactLabel = contact =>
  contact.name || contact.email || `#${contact.id}`;
</script>

<template>
  <div class="flex overflow-auto flex-col w-full h-full bg-n-background">
    <header class="flex gap-3 items-center px-6 py-4 border-b border-n-weak">
      <div class="flex-grow min-w-0">
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('REMINDERS.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">{{ t('REMINDERS.SUBTITLE') }}</p>
      </div>
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-help-circle"
        :label="t('REMINDERS.HELP')"
        @click="isGuideOpen = true"
      />
    </header>

    <div class="flex flex-col gap-6 p-6 mx-auto w-full max-w-3xl">
      <form
        class="flex flex-col gap-3 p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-2"
        @submit.prevent="createReminder"
      >
        <input
          v-model="form.title"
          type="text"
          :placeholder="t('REMINDERS.FORM.TITLE')"
          class="px-3 py-2 rounded-lg outline outline-1 outline-n-weak bg-n-background text-n-slate-12"
        />
        <textarea
          v-model="form.description"
          :placeholder="t('REMINDERS.FORM.DESCRIPTION')"
          rows="2"
          class="px-3 py-2 rounded-lg outline outline-1 outline-n-weak resize-none bg-n-background text-n-slate-12"
        />
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <select
            v-model="form.contact_id"
            class="px-3 py-2 rounded-lg outline outline-1 outline-n-weak bg-n-background text-n-slate-12"
          >
            <option :value="null">{{ t('REMINDERS.FORM.NO_CONTACT') }}</option>
            <option
              v-for="contact in contacts"
              :key="contact.id"
              :value="contact.id"
            >
              {{ contactLabel(contact) }}
            </option>
          </select>
          <input
            v-model="form.conversation_display_id"
            type="number"
            :placeholder="t('REMINDERS.FORM.CONVERSATION')"
            class="px-3 py-2 rounded-lg outline outline-1 outline-n-weak bg-n-background text-n-slate-12"
          />
        </div>
        <div class="flex gap-3 items-center">
          <input
            v-model="form.due_at"
            type="datetime-local"
            class="px-3 py-2 rounded-lg outline outline-1 outline-n-weak bg-n-background text-n-slate-12"
          />
          <button
            type="submit"
            :disabled="!form.title.trim() || uiFlags.isCreating"
            class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
          >
            {{ t('REMINDERS.FORM.SUBMIT') }}
          </button>
        </div>
      </form>

      <div v-if="uiFlags.isFetching" class="text-sm text-n-slate-11">
        {{ t('REMINDERS.LOADING') }}
      </div>
      <div
        v-else-if="!hasReminders"
        class="flex flex-col gap-3 items-center px-6 py-12 text-center rounded-xl outline outline-1 outline-dashed outline-n-weak"
      >
        <span
          class="grid rounded-full size-12 place-items-center bg-n-alpha-2 text-n-brand"
        >
          <span class="size-6 i-lucide-alarm-clock" />
        </span>
        <p class="text-base font-medium text-n-slate-12">
          {{ t('REMINDERS.EMPTY_STATE.TITLE') }}
        </p>
        <p class="max-w-sm text-sm text-n-slate-11">
          {{ t('REMINDERS.EMPTY_STATE.DESCRIPTION') }}
        </p>
        <button
          type="button"
          class="text-sm font-medium underline text-n-brand underline-offset-2"
          @click="isGuideOpen = true"
        >
          {{ t('REMINDERS.EMPTY_STATE.LEARN') }}
        </button>
      </div>
      <ul v-else class="flex flex-col gap-2 p-0 m-0 list-none">
        <li
          v-for="reminder in reminders"
          :key="reminder.id"
          class="flex gap-3 items-start p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-2"
        >
          <input
            type="checkbox"
            :checked="reminder.status === 'done'"
            class="mt-1 cursor-pointer"
            @change="toggleStatus(reminder)"
          />
          <div class="flex-grow min-w-0">
            <p
              class="text-sm font-medium text-n-slate-12"
              :class="{
                'line-through text-n-slate-10': reminder.status === 'done',
              }"
            >
              {{ reminder.title }}
            </p>
            <p
              v-if="reminder.description"
              class="text-sm whitespace-pre-line text-n-slate-11"
            >
              {{ reminder.description }}
            </p>
            <div class="flex flex-wrap gap-3 mt-1">
              <span v-if="reminder.due_at" class="text-xs text-n-slate-10">
                {{
                  t('REMINDERS.DUE_AT', { date: formatDate(reminder.due_at) })
                }}
              </span>
              <span
                v-if="reminder.contact"
                class="flex gap-1 items-center text-xs text-n-slate-10"
              >
                <span class="size-3 i-lucide-user" />
                {{ reminder.contact.name }}
              </span>
              <span
                v-if="reminder.conversation"
                class="flex gap-1 items-center text-xs text-n-slate-10"
              >
                <span class="size-3 i-lucide-message-circle" />
                {{ `#${reminder.conversation.display_id}` }}
              </span>
            </div>
          </div>
          <button
            type="button"
            class="flex-shrink-0 mt-0.5 size-4 i-lucide-trash-2 text-n-slate-10 hover:text-n-ruby-9"
            :title="t('REMINDERS.DELETE')"
            @click="deleteReminder(reminder.id)"
          />
        </li>
      </ul>
    </div>

    <FeatureGuide
      :open="isGuideOpen"
      :title="t('REMINDERS.TITLE')"
      icon="i-lucide-alarm-clock"
      :api-base="apiBase"
      :api-token="apiToken"
      :content="guideContent"
      @close="isGuideOpen = false"
    />
  </div>
</template>
